import { Suspense, type ReactNode } from 'react'
import { lazyRoute } from '@/lib/lazyRoute'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AppShell } from '@/components/AppShell'
import { SplashScreen } from '@/components/SplashScreen'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/utils'
import { LoginPage } from '@/pages/Login'
import { BadgeLoginPage } from '@/pages/BadgeLogin'
import { ForgotPasswordPage } from '@/pages/ForgotPassword'
import { ResetPasswordPage } from '@/pages/ResetPassword'
import { SetPasswordScreen } from '@/pages/SetPassword'
import { HomePage } from '@/pages/Home'
import { SubmissionsPage } from '@/pages/Submissions'
import { NewSubmissionPage } from '@/pages/NewSubmission'
import { TasksPage } from '@/pages/Tasks'
/*
 * `/5s` is a list now, not the form.
 *
 * A walk could be filed and then never read again — the percentage reached the
 * dashboard and the sheet itself was unreachable. The form moved to `/5s/nova`,
 * which is where the floating button points, and `/5s/:id` opens one.
 */
const EditSubmissionPage = lazyRoute(() =>
  import('@/pages/EditSubmission').then((m) => ({ default: m.EditSubmissionPage })),
)
const FiveSListPage = lazyRoute(() =>
  import('@/pages/FiveSList').then((m) => ({ default: m.FiveSListPage })),
)
const FiveSDetailPage = lazyRoute(() =>
  import('@/pages/FiveSDetail').then((m) => ({ default: m.FiveSDetailPage })),
)
import { FiveSPage } from '@/pages/FiveS'
import { QrRedirectPage } from '@/pages/QrRedirect'
import { Spinner } from '@/components/ui'
import { ModuleDisabled } from '@/components/ModuleDisabled'
import { moduleEnabled } from '@/lib/modules'
import type { ModuleName } from '@/types/api'

/*
 * Route-level code splitting.
 *
 * Recharts and the ZXing scanner are 872 KB between them and were arriving on
 * first paint — before a worker could even type a password, and on plant wifi.
 * Neither is needed to log in, read the dashboard or file a report:
 *
 *   - Analytics is manager-only and chart-heavy.
 *   - The scanner is opened deliberately, and only on a phone.
 *   - The whole admin section is for one or two people.
 *
 * Loaded on navigation instead. Everything a worker touches on a normal day
 * stays in the main bundle, so the common path did not get slower to make the
 * rare path smaller.
 */
const AnalyticsPage = lazyRoute(() =>
  import('@/pages/Analytics').then((m) => ({ default: m.AnalyticsPage })),
)
const ScanPage = lazyRoute(() => import('@/pages/Scan').then((m) => ({ default: m.ScanPage })))
const ProfilePage = lazyRoute(() => import('@/pages/Profile').then((m) => ({ default: m.ProfilePage })))
const QrStickersPage = lazyRoute(() =>
  import('@/pages/QrStickers').then((m) => ({ default: m.QrStickersPage })),
)
const AdminLayout = lazyRoute(() =>
  import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
)
const AdminUsersPage = lazyRoute(() =>
  import('@/pages/admin/Users').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminPlantsPage = lazyRoute(() =>
  import('@/pages/admin/Plants').then((m) => ({ default: m.AdminPlantsPage })),
)

const AdminShiftsPage = lazyRoute(() =>
  import('@/pages/admin/Shifts').then((m) => ({ default: m.AdminShiftsPage })),
)

const AdminWorkCentersPage = lazyRoute(() =>
  import('@/pages/admin/WorkCenters').then((m) => ({ default: m.AdminWorkCentersPage })),
)
/*
 * Lazy, and it earns it: this page carries the image lightbox, which nobody
 * needs until they open a submission with a photo on it. Imported eagerly it
 * sat in the bundle every worker downloads before their first screen.
 */
const SubmissionDetailPage = lazyRoute(() =>
  import('@/pages/SubmissionDetail').then((m) => ({ default: m.SubmissionDetailPage })),
)
/*
 * Lazy for the reason the rest of this list is: the guide is read once, on the
 * day somebody is handed an account, and never again by most people. It is a
 * page of prose with no dependency a worker's daily path does not already
 * carry, so the chunk is small — but it is still a page nobody opens on the
 * shift where they file a report.
 */
const GuidePage = lazyRoute(() => import('@/pages/Guide').then((m) => ({ default: m.GuidePage })))
const AdminIntegrationPage = lazyRoute(() =>
  import('@/pages/admin/Integration').then((m) => ({ default: m.AdminIntegrationPage })),
)
const AdminSettingsPage = lazyRoute(() =>
  import('@/pages/admin/Settings').then((m) => ({ default: m.AdminSettingsPage })),
)
const AdminTemplatesPage = lazyRoute(() =>
  import('@/pages/admin/Templates').then((m) => ({ default: m.AdminTemplatesPage })),
)
const AdminActivityLogPage = lazyRoute(() =>
  import('@/pages/admin/ActivityLog').then((m) => ({ default: m.AdminActivityLogPage })),
)
/*
 * The owner console, lazily loaded as one chunk.
 *
 * Nobody but the vendor ever opens it, and it carries its own shell, charts and
 * forms — none of which belong in the bundle a worker downloads on plant wifi
 * to file a report.
 */
const OwnerShell = lazyRoute(() =>
  import('@/pages/owner/OwnerShell').then((m) => ({ default: m.OwnerShell })),
)
const OwnerDashboardPage = lazyRoute(() =>
  import('@/pages/owner/OwnerDashboard').then((m) => ({ default: m.OwnerDashboardPage })),
)
const OwnerCompaniesPage = lazyRoute(() =>
  import('@/pages/owner/Companies').then((m) => ({ default: m.OwnerCompaniesPage })),
)
const OwnerCompanyWorkspacePage = lazyRoute(() =>
  import('@/pages/owner/workspace/CompanyWorkspace').then((m) => ({
    default: m.OwnerCompanyWorkspacePage,
  })),
)
const OwnerTeamPage = lazyRoute(() =>
  import('@/pages/owner/Team').then((m) => ({ default: m.OwnerTeamPage })),
)
const OwnerAnalyticsPage = lazyRoute(() =>
  import('@/pages/owner/Analytics').then((m) => ({ default: m.OwnerAnalyticsPage })),
)
const OwnerHealthPage = lazyRoute(() =>
  import('@/pages/owner/Health').then((m) => ({ default: m.OwnerHealthPage })),
)

/** Blocks a route until the session is known, then redirects if there is none. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) return <Spinner label="Provjera sesije…" />
  if (!user) return <Navigate to="/prijava" replace />

  /*
   * Signed in with an activation code somebody else read out.
   *
   * This replaces the application rather than redirecting to a route, so there
   * is no URL to navigate away from and nothing to go back to. The API refuses
   * everything else anyway (423 from EnsurePasswordIsSet) — showing the rest of
   * the app would just be a screen full of errors.
   */
  if (user.mustSetPassword) return <SetPasswordScreen />

  return <>{children}</>
}

function RequireManager({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  // Redirect rather than show a bare 403 — a worker following an old link
  // should land somewhere useful, not on a dead end.
  if (!user?.isManager) return <Navigate to="/" replace />

  return <>{children}</>
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  if (user?.role !== 'ADMIN') return <Navigate to="/" replace />

  return <>{children}</>
}

/**
 * A part of the application the company may not have.
 *
 * Renders the explanation in place of the page rather than redirecting: a
 * worker who followed a link or scanned a sticker should be told why there is
 * nothing here, not dropped on the home screen wondering whether the scan
 * worked. A layout route, so every screen of a module is covered by one
 * declaration instead of a wrapper remembered on each.
 */
function RequireModule({ module }: { module: ModuleName }) {
  const { user } = useAuth()

  if (!moduleEnabled(user?.company?.modules, module)) return <ModuleDisabled module={module} />

  return <Outlet />
}

/**
 * The owner console, which is the one place that crosses customers.
 *
 * Also closed during a support session, matching the server. The session runs
 * as the customer's user so `isOwner` is already false — but the reason is
 * worth stating rather than relying on: a cross-customer view has no business
 * being on a screen the customer's own administrator might be watching.
 */
function RequireOwner({ children }: { children: ReactNode }) {
  const { user, isLoading, impersonation } = useAuth()

  if (isLoading) return <Spinner label="Provjera sesije…" />
  if (!user) return <Navigate to="/prijava" replace />
  if (!user.isOwner || impersonation) return <Navigate to="/" replace />

  return <>{children}</>
}

/**
 * Sends an owner to their own console instead of a factory dashboard.
 *
 * An owner has no submissions, no 5S walks and no work centres — the customer
 * app is furniture to them, and its home screen would greet them by a role they
 * do not have. During a support session this does *not* fire: `isOwner` is
 * false while acting as the customer's user, which is exactly right, because
 * that session's whole purpose is to see what the customer sees.
 */
function HomeForRole() {
  const { user } = useAuth()

  return user?.isOwner ? <Navigate to="/vlasnik" replace /> : <HomePage />
}

export default function App() {
  const { isLoading } = useAuth()

  return (
    <>
      {/* Overlays everything until the fonts, assets and session check have all
          settled. It removes itself; nothing below needs to know about it. */}
      <SplashScreen sessionReady={!isLoading} />

      <Routes>
        <Route path="/prijava" element={<LoginPage />} />

        {/* Where a scanned worker badge lands. Outside RequireAuth: the whole
            point is that there is no session yet. */}
        <Route path="/prijava/kartica/:token" element={<BadgeLoginPage />} />

        {/* Password recovery, both halves signed out for the same reason. */}
        <Route path="/prijava/zaboravljena-lozinka" element={<ForgotPasswordPage />} />
        <Route path="/prijava/nova-lozinka" element={<ResetPasswordPage />} />

        {/*
          The owner console — its own shell, outside AppShell entirely.

          Not a page inside the customer application: an owner is not a factory
          worker, so the whole customer navigation is furniture to them, and
          leaving them inside it makes "which company am I looking at" a
          question with no answer on screen. Acting in the wrong context is the
          failure mode this separation exists to prevent.
        */}
        <Route
          path="/vlasnik"
          element={
            <RequireOwner>
              <Suspense fallback={<Spinner label="Učitavanje panela…" />}>
                <OwnerShell />
              </Suspense>
            </RequireOwner>
          }
        >
          <Route
            element={
              <Suspense fallback={<Spinner label="Učitavanje stranice…" />}>
                <Outlet />
              </Suspense>
            }
          >
            <Route index element={<OwnerDashboardPage />} />
            <Route path="firme" element={<OwnerCompaniesPage />} />
            {/* One customer's workspace; the tab is part of the URL so a link
                pasted into a ticket opens the tab it was copied from. */}
            <Route path="firme/:companyId/:tab?" element={<OwnerCompanyWorkspacePage />} />
            <Route path="analitika" element={<OwnerAnalyticsPage />} />
            <Route path="tim" element={<OwnerTeamPage />} />
            {/* The page was "Korisnici" before it became the team; keep the old
                address landing somewhere that exists. */}
            <Route path="korisnici" element={<Navigate to="/vlasnik/tim" replace />} />
            <Route path="sistem" element={<OwnerHealthPage />} />

            {/*
              The same profile screen every other user gets.

              Owners were sent to their console and then had nowhere to change
              their own password, turn on a second factor, or end a session on
              a lost laptop — the account with the most reach in the
              installation was the one account with no security controls. It is
              the same component rather than a second copy: everything on it
              applies to an owner exactly as it does to anybody else.
            */}
            <Route path="profil" element={<ProfilePage />} />
          </Route>
        </Route>

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          {/* One boundary around the routed area rather than per route — the
              shell stays painted while a lazy chunk arrives, so navigation
              never flashes an empty screen. */}
          <Route
            element={
              <Suspense fallback={<Spinner label="Učitavanje stranice…" />}>
                <Outlet />
              </Suspense>
            }
          >
            {/* Owners are sent to their own console; everybody else gets the
                factory dashboard. See HomeForRole. */}
            <Route path="/" element={<HomeForRole />} />
            {/* The paths each module owns — the same list `moduleForPath`
                keeps for the 403 listener in AppShell. */}
            <Route element={<RequireModule module="submissions" />}>
              <Route path="/prijave" element={<SubmissionsPage />} />
              <Route path="/prijave/nova" element={<NewSubmissionPage />} />
              <Route path="/prijave/:id/izmjena" element={<EditSubmissionPage />} />
              <Route path="/prijave/:id" element={<SubmissionDetailPage />} />
              <Route path="/zadaci" element={<TasksPage />} />
            </Route>
            <Route element={<RequireModule module="fiveS" />}>
              <Route path="/5s" element={<FiveSListPage />} />
              <Route path="/5s/nova" element={<FiveSPage />} />
              <Route path="/5s/:id/izmjena" element={<FiveSPage />} />
              <Route path="/5s/:id" element={<FiveSDetailPage />} />
            </Route>
            <Route path="/skeniraj" element={<ScanPage />} />
            <Route path="/profil" element={<ProfilePage />} />

            {/* Every role has a guide, and the one who most needs it is the
                worker — so this sits with the ordinary routes and carries no
                role guard. Which sections it holds is the server's answer. */}
            <Route path="/uputstvo" element={<GuidePage />} />

            {/* Where a scanned sticker lands. Must stay inside the authenticated
                shell so the user is sent to login and back if their session
                lapsed since they last opened the app. */}
            <Route path="/qr/:token" element={<QrRedirectPage />} />

            <Route element={<RequireModule module="analytics" />}>
              <Route
                path="/analitika"
                element={
                  <RequireManager>
                    <AnalyticsPage />
                  </RequireManager>
                }
              />
            </Route>

            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="/admin/korisnici" replace />} />
              <Route path="korisnici" element={<AdminUsersPage />} />
              <Route path="pogoni" element={<AdminPlantsPage />} />
              <Route path="radni-centri" element={<AdminWorkCentersPage />} />
              <Route path="smjene" element={<AdminShiftsPage />} />
              <Route element={<RequireModule module="fiveS" />}>
                <Route path="obrasci" element={<AdminTemplatesPage />} />
              </Route>
              <Route element={<RequireModule module="mes" />}>
                <Route path="integracija" element={<AdminIntegrationPage />} />
              </Route>
              <Route path="postavke" element={<AdminSettingsPage />} />
              <Route path="nalepnice" element={<QrStickersPage />} />
              <Route path="dnevnik" element={<AdminActivityLogPage />} />
            </Route>

            {/* The sticker page used to live here; keep the old path working so
                any bookmark or printed instruction still lands correctly. */}
            <Route path="/nalepnice" element={<Navigate to="/admin/nalepnice" replace />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/*
        Toasts as iOS-style glass banners.

        `richColors` is deliberately off: it paints the whole toast a solid
        green or red, which cannot be translucent and reads as a web alert
        rather than a system banner. The status is carried by the icon's tint
        instead, which is how the platform does it — and it keeps the body text
        on a neutral background where it stays legible in both themes.
      */}
      <Toaster
        position="top-center"
        closeButton
        offset={16}
        toastOptions={{
          classNames: {
            toast: cn(
              'group rounded-2xl border border-black/5 dark:border-white/10',
              'bg-card/80 supports-[backdrop-filter]:bg-card/65',
              'backdrop-blur-2xl backdrop-saturate-150 shadow-lg',
              'text-foreground gap-3',
            ),
            title: 'font-semibold',
            description: 'text-muted-foreground',
            actionButton: 'rounded-full bg-primary text-primary-foreground font-medium',
            cancelButton: 'rounded-full bg-muted text-foreground',
            closeButton: 'rounded-full border-black/5 bg-card/80 backdrop-blur dark:border-white/10',
            // Tint the icon only — see the note above.
            success: '[&_[data-icon]]:text-emerald-500',
            error: '[&_[data-icon]]:text-rose-500',
            warning: '[&_[data-icon]]:text-amber-500',
            info: '[&_[data-icon]]:text-sky-500',
          },
        }}
      />
    </>
  )
}
